import { useMemo, useState } from 'react';
import { apiClient, ApiError } from '@/lib/api/client';

interface Props {
  /** IANA timezone string, or null if user hasn't set one. */
  timezone: string | null;
  /** Local-time "HH:MM" start, or null. */
  quietStart: string | null;
  /** Local-time "HH:MM" end, or null. */
  quietEnd: string | null;
  /** Called after a successful save / disable. Parent refetches user state. */
  onSaved: () => void;
}

/**
 * Quiet hours + timezone form. User-scoped (not workspace-scoped, despite
 * being reachable from the workspace settings drawer per audit P1) — the
 * backend stores these on the users row. Lifted out of TelegramModal so users
 * who haven't linked Telegram can still set their TZ + quiet hours (was
 * gated behind link-flow previously).
 *
 * Uses apiClient.telegramUpdate which writes user-level fields — the name is
 * legacy from when these settings only mattered for TG delivery; they now
 * also apply to email digest batching once that ships.
 */
export function QuietHoursForm({ timezone, quietStart, quietEnd, onSaved }: Props) {
  const browserTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }, []);
  const [tz, setTz] = useState(timezone ?? browserTz);
  const [start, setStart] = useState(quietStart ?? '');
  const [end, setEnd] = useState(quietEnd ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    (tz || '') !== (timezone ?? '') ||
    (start || '') !== (quietStart ?? '') ||
    (end || '') !== (quietEnd ?? '');

  const enabled = Boolean(start && end);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.telegramUpdate({
        timezone: tz || null,
        quiet_start_local: start || null,
        quiet_end_local: end || null,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.telegramUpdate({
        quiet_start_local: null,
        quiet_end_local: null,
      });
      setStart('');
      setEnd('');
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-border bg-background/40 p-3">
      <div className="space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Quiet hours
        </div>
        <div className="text-xs text-muted-foreground">
          During quiet hours, even urgent DMs are queued and flushed at the end of the window.
          Batches (≥5 events or 10-min window) are also held.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            From
          </span>
          <input
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="mt-1 block w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-ring focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            To
          </span>
          <input
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="mt-1 block w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-ring focus:outline-none"
          />
        </label>
      </div>
      <label className="block">
        <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Timezone (IANA)
        </span>
        <input
          type="text"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          placeholder={browserTz}
          className="mt-1 block w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-ring focus:outline-none"
        />
        <span className="mt-1 block text-[10px] text-muted-foreground">
          Detected: <code className="font-mono">{browserTz}</code>
        </span>
      </label>
      {err && (
        <div role="alert" className="text-xs text-destructive">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={
            !dirty ||
            busy ||
            (Boolean(start) && !end) ||
            (!start && Boolean(end))
          }
          className="flex-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? 'Saving…' : enabled ? 'Save quiet hours' : 'Set times to enable'}
        </button>
        {(quietStart || quietEnd) && (
          <button
            type="button"
            onClick={disable}
            disabled={busy}
            className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground disabled:opacity-50"
          >
            Disable
          </button>
        )}
      </div>
    </div>
  );
}
